import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Wallet, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { unitLabel, inr, fmtDate, fmtDateTime, StatusBadge } from "@/lib/utils";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function MyOrders() {
  const { t, tAny } = useLang();
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payConfig, setPayConfig] = useState(null);

  const load = () =>
    api
      .get("/orders/mine")
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
    api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => {});
  }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const cancelOrder = async () => {
    const reason = window.prompt("Tell us why you are cancelling this order (optional):");
    if (reason === null) return;
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    try {
      const { data } = await api.post(`/orders/${selected.id}/cancel`, { reason: reason.trim() });
      setSelected({ ...selected, ...data });
      toast.success("Order cancelled");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const photoUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}?auth=${token}` : null);

  const payOnline = async (order) => {
    try {
      const { data: config } = await api.get("/payments/config");
      setPayConfig(config);
      if (config.razorpay_enabled) {
        const ok = await loadRazorpay();
        if (!ok) return toast.error("Could not load payment gateway");
        const { data: rpo } = await api.post("/payments/razorpay/order", { order_id: order.id });
        const rzp = new window.Razorpay({
          key: rpo.key_id,
          amount: rpo.amount,
          currency: "INR",
          order_id: rpo.razorpay_order_id,
          name: config.business_name,
          prefill: { name: rpo.customer_name, contact: rpo.customer_mobile },
          theme: { color: "#0B192C" },
          handler: async (res) => {
            try {
              await api.post("/payments/razorpay/verify", { order_id: order.id, ...res });
              toast.success("Payment verified. Thank you!");
              setPayFor(null);
              setSelected(null);
              load();
            } catch (e) {
              toast.error(fmtErr(e));
            }
          },
        });
        rzp.open();
      } else {
        setPayFor(order);
      }
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const markUpiPaid = async () => {
    try {
      await api.post("/payments/upi-intent", { order_id: payFor.id });
      toast.success("Submitted. The company will verify your UPI payment shortly.");
      setPayFor(null);
      if (selected) openDetail(selected.id);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const upiValue = payFor && payConfig?.upi_id
    ? `upi://pay?pa=${encodeURIComponent(payConfig.upi_id)}&pn=${encodeURIComponent(payConfig.business_name || "Sona Fertilizer")}&am=${payFor.payment.due}&tn=${encodeURIComponent(payFor.order_number)}`
    : "";

  return (
    <div className="space-y-6" data-testid="my-orders-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{t("myOrdersTitle")}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("myOrdersSub")}</p>
      </div>

      <div className="sf-card overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr>
              <th className="sf-th">{t("orderNo")}</th>
              <th className="sf-th">{t("date")}</th>
              <th className="sf-th">{t("products")}</th>
              <th className="sf-th">{t("total")}</th>
              <th className="sf-th">{t("paid")}</th>
              <th className="sf-th">{t("due")}</th>
              <th className="sf-th">{t("payment")}</th>
              <th className="sf-th">{t("deliveryDate")}</th>
              <th className="sf-th">{t("status")}</th>
              <th className="sf-th"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="sf-tr cursor-pointer" data-testid={`order-row-${o.order_number}`} onClick={() => openDetail(o.id)}>
                <td className="sf-td font-mono-sf font-semibold text-slate-900">{o.order_number}</td>
                <td className="sf-td">{fmtDate(o.created_at)}</td>
                <td className="sf-td">{o.items.length}</td>
                <td className="sf-td">{o.payment.total > 0 ? inr(o.payment.total) : "—"}</td>
                <td className="sf-td">{inr(o.payment.paid)}</td>
                <td className="sf-td font-semibold">{inr(o.payment.due)}</td>
                <td className="sf-td">
                  <StatusBadge status={o.payment.status} />
                </td>
                <td className="sf-td">{o.delivery?.date ? fmtDate(o.delivery.date) : "—"}</td>
                <td className="sf-td">
                  <StatusBadge status={o.status} />
                </td>
                <td className="sf-td text-right">
                  <button data-testid={`view-order-btn-${o.order_number}`} className="sf-btn-outline !py-1 !px-2.5 text-xs" onClick={(e) => { e.stopPropagation(); openDetail(o.id); }}>
                    {t("view")}
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (              <tr>
                <td className="sf-td text-slate-400" colSpan={9}>
                  No orders yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="order-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-mono-sf">{selected?.order_number}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={selected.status} />
                <StatusBadge status={selected.payment.status} testid={`payment-badge-${selected.order_number}`} />
                {['Order Placed', 'Under Review', 'Quantity Modified'].includes(selected.status) && (
                  <button
                    data-testid={`cancel-order-btn-${selected.order_number}`}
                    className="sf-btn-danger !py-1.5 !px-3.5 text-xs ml-auto"
                    onClick={cancelOrder}
                  >
                    {t("cancelOrder")}
                  </button>
                )}
              </div>

              <table className="w-full">
                <thead>
                  <tr>
                    <th className="sf-th">{t("company")}</th>
                    <th className="sf-th">{t("product")}</th>
                    <th className="sf-th">{t("requestedQty")}</th>
                    <th className="sf-th">{t("approved")}</th><th className="sf-th">Status</th><th className="sf-th">Reason</th>
                    <th className="sf-th text-right">{t("amount")}</th><th className="sf-th">Status</th><th className="sf-th">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.items.map((l) => (
                    <tr key={l.id} data-testid={`detail-line-${l.id}`}>
                      <td className="sf-td">{l.company_name}</td>
                      <td className="sf-td font-semibold text-slate-900">{l.product_name}</td>
                      <td className="sf-td">
                        {l.requested_qty} {unitLabel(l.requested_unit)}
                      </td>
                      <td className="sf-td">{l.approved_qty ? `${l.approved_qty} ${unitLabel(l.approved_unit)}` : "—"}</td><td className="sf-td"><StatusBadge status={l.line_status} /></td><td className="sf-td text-sm text-slate-600">{l.admin_note || "—"}</td>
                      <td className="sf-td text-right">{l.line_total != null ? inr(l.line_total) : t("callForRate")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 p-4" data-testid="payment-summary-box">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
                    <Wallet className="h-4 w-4" /> {t("payment")}
                  </div>
                  <div className="space-y-1 text-sm text-slate-700">
                    <div className="flex justify-between">
                      <span>{t("total")}</span>
                      <span className="font-semibold">{selected.payment.total > 0 ? inr(selected.payment.total) : t("ratePending")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("paid")}</span>
                      <span className="font-semibold text-emerald-700">{inr(selected.payment.paid)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("due")}</span>
                      <span className="font-bold text-rose-700" data-testid="order-due-amount">{inr(selected.payment.due)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status / Method</span>
                      <span>
                        {selected.payment.status} · {selected.payment.method || "—"}
                      </span>
                    </div>
                  </div>
                  {selected.payment.due > 0 && (
                    <button data-testid="pay-online-btn" className="sf-btn-blue w-full mt-3" onClick={() => payOnline(selected)}>
                      {t("payOnline")}
                    </button>                  )}
                  {(selected.payments || []).length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500 space-y-1">
                      {(selected.payments || []).map((p) => (
                        <div key={p.id} className="flex justify-between" data-testid={`payment-record-${p.id}`}>
                          <span>
                            {fmtDateTime(p.created_at)} · {p.method} {p.mode === "upi_qr" ? "(unverified)" : ""}
                          </span>
                          <span className="font-semibold">{inr(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 p-4" data-testid="delivery-summary-box">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
                    <Truck className="h-4 w-4" /> {t("deliveryDispatch")}
                  </div>
                  <div className="space-y-1 text-sm text-slate-700">
                    <div className="flex justify-between">
                      <span>Status</span>
                      <StatusBadge status={selected.delivery?.status || "Pending"} />
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery date</span>
                      <span>{selected.delivery?.date ? fmtDate(selected.delivery.date) : "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Vehicle number</span>
                      <span className="font-mono-sf">{selected.delivery?.vehicle_number || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Driver</span>
                      <span>
                        {selected.delivery?.driver_name || "—"}
                        {selected.delivery?.driver_phone ? ` · ${selected.delivery.driver_phone}` : ""}
                      </span>
                    </div>
                    {selected.delivery?.notes && <div className="text-xs text-slate-500 pt-1">Note: {selected.delivery.notes}</div>}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4" data-testid="timeline-box">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
                  <History className="h-4 w-4" /> {t("orderTimeline")}
                </div>
                <ol className="space-y-2">
                  {(selected.timeline || []).slice().reverse().map((ev, i) => (
                    <li key={i} className="flex gap-3 text-sm" data-testid={`timeline-item-${i}`}>
                      <span className="h-2 w-2 rounded-full bg-[#1E3E62] mt-1.5 shrink-0" />
                      <div>
                        <span className="font-semibold text-slate-900">{tAny(ev.event)}</span>
                        {ev.detail && <span className="text-slate-600"> — {ev.detail}</span>}
                        <div className="text-xs text-slate-400">{fmtDateTime(ev.at)}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="upi-payment-modal">
          <DialogHeader>
            <DialogTitle>Pay Online</DialogTitle>
          </DialogHeader>
          {payFor && (payConfig?.upi_id || payConfig?.payment_qr_url || payConfig?.bank_account_number) && (
            <div className="text-center space-y-4">
              {payConfig?.payment_qr_url ? (
                <div className="inline-block p-3 bg-white border border-slate-200 rounded-xl">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${payConfig.payment_qr_url}`} alt="Payment QR" className="h-48 w-48 object-contain" data-testid="payment-qr-image" />
                </div>
              ) : payConfig?.upi_id ? (
                <div className="inline-block p-3 bg-white border border-slate-200 rounded-xl">
                  <QRCodeSVG value={upiValue} size={180} data-testid="upi-qr-code" />
                </div>
              ) : null}
              <div className="text-sm text-slate-600">
                Scan with any UPI app to pay <span className="font-bold text-slate-900">{inr(payFor.payment.due)}</span> for{" "}
                <span className="font-mono-sf font-semibold">{payFor.order_number}</span>
                {payConfig?.upi_id && <div className="text-xs text-slate-400 mt-1">UPI ID: {payConfig.upi_id}</div>}
              </div>
              {payConfig?.bank_account_number && (
                <div className="text-left text-sm rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="bank-transfer-box">
                  <div className="font-bold text-slate-900 mb-1">Or pay by bank transfer</div>
                  <div className="text-slate-700">A/C Name: <span className="font-semibold">{payConfig.bank_holder_name || "—"}</span></div>
                  <div className="text-slate-700">A/C No: <span className="font-mono-sf font-semibold">{payConfig.bank_account_number}</span></div>
                  <div className="text-slate-700">Bank: <span className="font-semibold">{payConfig.bank_name || "—"}</span></div>
                </div>
              )}
              <button data-testid="upi-paid-btn" className="sf-btn-primary w-full" onClick={markUpiPaid}>
                I have completed payment
              </button>
              <p className="text-xs text-slate-400">The company verifies payments manually before updating your order.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
